import { useEffect, useRef, useState } from 'react';

export const useIsElementFocused = <TRef extends HTMLElement>(rootId: string) => {
    const [isFocused, setIsFocused] = useState(false);
    const wrapperRef = useRef<TRef>(null);
    const isClickingInsideRef = useRef(false);

    useEffect(() => {
        const mouseDownListener = (e: MouseEvent) => {
            const inWrapper = wrapperRef.current?.contains(e.target as Node);
            // Track if we're clicking inside the wrapper
            if (inWrapper) {
                isClickingInsideRef.current = true;
            }
        };

        const mouseUpListener = () => {
            // Reset the clicking flag after a short delay
            // This prevents the blur event from incorrectly setting isFocused to false
            setTimeout(() => {
                isClickingInsideRef.current = false;
            }, 50);
        };

        const clickListener = (e: MouseEvent) => {
            const inWrapper = wrapperRef.current?.contains(e.target as Node);
            if (!inWrapper && isFocused) {
                setIsFocused(false);
            }
        };

        const blurListener = (e: FocusEvent) => {
            // Safari workaround: Don't process blur if we just clicked inside
            if (isClickingInsideRef.current) {
                return;
            }

            // Check if the blur event is from an element inside our wrapper
            const inWrapper = wrapperRef.current?.contains(e.target as Node);
            if (inWrapper && isFocused) {
                // Check if the new focus target is outside our wrapper
                const newTarget = e.relatedTarget as Node;
                if (!newTarget || !wrapperRef.current?.contains(newTarget)) {
                    setIsFocused(false);
                }
            }
        };

        const rootElement = document.getElementById(rootId);
        if (rootElement) {
            rootElement.addEventListener('mousedown', mouseDownListener);
            rootElement.addEventListener('mouseup', mouseUpListener);
            rootElement.addEventListener('click', clickListener);
            rootElement.addEventListener('focusout', blurListener);

            return () => {
                rootElement.removeEventListener('mousedown', mouseDownListener);
                rootElement.removeEventListener('mouseup', mouseUpListener);
                rootElement.removeEventListener('click', clickListener);
                rootElement.removeEventListener('focusout', blurListener);
            };
        }
    }, [isFocused, rootId]);

    return { isFocused, wrapperRef, setIsFocused };
};
